import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { AlertTriangle, Car, GitCompareArrows, Route, Upload } from "lucide-react";
import { Bar, BarChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { api } from "@/lib/api";
import { useT } from "@/lib/i18n";
import { KIND_LABEL, fmtDateTime } from "@/lib/format";
import { KpiCard, PageHeader, ScoreRing, Spinner, StatusBadge } from "@/components/shared";
import { FleetMap } from "@/components/map/FleetMap";
import { AlertStrip } from "@/components/AlertStrip";

export default function Dashboard() {
  const t = useT();
  const { data, isLoading } = useQuery({ queryKey: ["dashboard"], queryFn: async () => (await api.get("/dashboard")).data, refetchInterval: 15000 });
  const live = useQuery({ queryKey: ["live-mini"], queryFn: async () => (await api.get("/positions/live")).data, refetchInterval: 10000 });
  if (isLoading || !data) return <Spinner />;
  const s = data.byStatus || {};
  return (
    <div data-testid="dashboard-page">
      <PageHeader title={t("Fleet overview")} subtitle={t("Physical journeys vs declared Uber trips · last 24 h")} />
      <AlertStrip />
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
        <KpiCard testId="kpi-vehicles-online" label={t("Vehicles online")} value={<>{data.vehiclesOnline}<span className="text-slate-500 text-lg">/{data.vehiclesTotal}</span></>} sub={t("Telemetry pulse < 5 min")} icon={<Car className="h-4 w-4" />} accent="#10B981" pulse />
        <KpiCard testId="kpi-journeys" label={t("GPS journeys")} value={data.journeys24h} sub={`${data.activeJourneys} ${t("active")} · ${data.distanceKm24h} km`} icon={<Route className="h-4 w-4" />} />
        <KpiCard testId="kpi-uber-trips" label={t("Uber trips declared")} value={data.uberTrips24h} sub={t("Imported / declared")} icon={<Upload className="h-4 w-4" />} accent="#A855F7" />
        <KpiCard testId="kpi-match-rate" label={t("Match rate")} value={`${data.matchRate}%`} sub={`${s.MATCHED || 0} ${t("matched")} · ${s.POSSIBLE_MATCH || 0} ${t("possible")}`} icon={<GitCompareArrows className="h-4 w-4" />} accent="#06B6D4" />
        <Link to="/reconciliation?pending=1"><KpiCard testId="kpi-pending-review" label={t("Requires review")} value={data.pendingReview} sub={`${s.NEEDS_REVIEW || 0} ${t("review")} · ${s.UNMATCHED || 0} ${t("unmatched")}`} icon={<AlertTriangle className="h-4 w-4" />} accent="#F59E0B" pulse={data.pendingReview > 0} /></Link>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 mt-6">
        <div className="lg:col-span-7 tg-card p-1 h-[420px] tg-enter">
          <FleetMap positions={live.data || []} />
        </div>
        <div className="lg:col-span-5 flex flex-col gap-6">
          <div className="tg-card p-5 tg-enter">
            <div className="tg-label mb-3">Journeys per hour (London)</div>
            <div className="h-36 min-w-0" style={{ minHeight: 144 }}>
              <ResponsiveContainer width="100%" height={144}>
                <BarChart data={data.hourly}>
                  <XAxis dataKey="hour" tick={{ fill: "#64748b", fontSize: 10, fontFamily: "JetBrains Mono" }} axisLine={false} tickLine={false} />
                  <YAxis hide />
                  <Tooltip contentStyle={{ background: "#182030", border: "1px solid #26334d", fontSize: 12 }} cursor={{ fill: "rgba(59,130,246,0.08)" }} />
                  <Bar dataKey="journeys" fill="#3b82f6" radius={[3, 3, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
          <div className="tg-card p-5 flex-1 tg-enter" data-testid="dashboard-recent-list">
            <div className="flex items-center justify-between mb-3"><div className="tg-label">Latest reconciliation</div><Link to="/reconciliation" className="text-xs text-blue-400 hover:underline" data-testid="dashboard-view-all-link">View inbox →</Link></div>
            <div className="space-y-2">
              {data.recent.map((r: any) => (
                <Link key={r.id} to={`/reconciliation/${r.id}`} data-testid={`recent-rec-${r.id}`} className="flex items-center gap-3 rounded-md px-2 py-1.5 hover:bg-[#1f2a3e] transition-colors">
                  <ScoreRing score={r.score} size={36} />
                  <div className="min-w-0 flex-1">
                    <div className="text-sm text-slate-200 truncate">{KIND_LABEL[r.kind] || r.kind}</div>
                    <div className="font-mono text-[11px] text-slate-500">{r.plate || "—"} · {r.externalTripId || "no declared trip"} · {fmtDateTime(r.eventTime)}</div>
                  </div>
                  <StatusBadge status={r.status} />
                </Link>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
